import { B2bEvent } from '@/api/b2b';
import { Paginator } from '@/components/Elements/Paginator/Paginator';
import { Card } from '@/components/ui/elements/card';
import { Heading2 } from '@/components/ui/typography/Typography';
import { PaginatedResource } from '@/utils/api';
import { formatApiDate } from '@/utils/format';
import { formatNumber } from '@/utils/numbers-utils';
import { UseQueryResult } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

type EventColumnProps = {
  title: string;
  useData: (page: number) => UseQueryResult<PaginatedResource<B2bEvent>>;
  emptyLabel: string;
  testid?: string;
};

export const EventColumn = ({ title, useData, emptyLabel, testid }: EventColumnProps) => {
  const [page, setPage] = useState(1);
  const { data, isPending } = useData(page);
  const events = data?.results ?? [];
  const total = data?.paginator.total ?? 0;
  const lastPage = data?.paginator.lastPage ?? 1;

  // A refresh (e.g. a booking moving out of this column) can shrink the
  // list out from under the current page — recover to the last valid page
  // instead of stranding the user on a blank one. Skipped while loading:
  // `lastPage` falls back to 1 before a not-yet-fetched page's real data
  // arrives, which would otherwise bounce every page > 1 back to page 1 the
  // instant it's requested.
  useEffect(() => {
    if (isPending) return;
    if (lastPage >= 1 && page > lastPage) {
      setPage(lastPage);
    }
  }, [isPending, page, lastPage]);

  return (
    <Card className="flex flex-col p-3" data-testid={testid}>
      <Heading2>{title}</Heading2>
      <div className="mt-3 flex-1 space-y-2">
        {isPending ? (
          <div className="h-12 animate-pulse rounded bg-muted" />
        ) : total === 0 ? (
          <div className="rounded border border-dashed p-6 text-center text-sm text-muted-foreground">
            {emptyLabel}
          </div>
        ) : (
          events.map((event) => (
            <Link
              key={event.event_id}
              to={`/events/event/${event.event_id}/overview`}
              className="block rounded border p-2 hover:bg-muted"
            >
              <div className="flex justify-between">
                <span className="font-medium">
                  #{event.event_id} {event.name || 'Event'}
                </span>
                <span className="tabular-nums">{formatNumber(event.total, 'currency')}</span>
              </div>
              <div className="text-xs text-muted-foreground">
                {formatApiDate(event.start_at ?? '')}
              </div>
            </Link>
          ))
        )}
      </div>
      {lastPage > 1 && (
        <Paginator page={page} lastPage={lastPage} onPageChange={setPage} className="mt-2" />
      )}
    </Card>
  );
};
